import { FC, useEffect, useState } from 'react';
import logo from '../../assets/img/passplay_logo.png';
import { Button, Menu, MenuItem } from '@nike/eds';
import { CaretDown, CaretUp } from '@nike/nike-design-system-icons';
import styles from '../layout.module.css';
import { useI18nContext } from 'context/language/use-i18n-context';
import ts from '../i18n';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from 'context/user';
import { usePassPolicies } from 'context/PassPolicy';
import { AppConfig } from 'utils/constants';
import { useSponsorContext } from 'context/Sponsor';

export const CommandBar: FC = () => {
  type MenuState = {
    isOpen: boolean;
    activeMenu: 'mypasses' | 'myaccount' | 'myaccess' | 'family' | 'approvals';
  };
  type MenuAccess = {
    showMyPasses: boolean;
    showMyAccount: boolean;
    showMyAccess: boolean;
    showFamily: boolean;
  };
  const [menuState, setMenuState] = useState<MenuState>({ isOpen: false, activeMenu: 'mypasses' });
  const [menuAccess, setMenuAccess] = useState<MenuAccess>({
    showMyPasses: false,
    showMyAccount: false,
    showMyAccess: false,
    showFamily: false,
  });
  const { i18nString } = useI18nContext();

  const navigate = useNavigate();
  const user = useUserContext();
  const location = useLocation();
  const passPolicesContext = usePassPolicies();
  const { sponsor } = useSponsorContext();
  const isETW = user?.passplayUser?.cponsorTypeId === 5 || sponsor?.sponsorTypeId === 5;

  const handleMenuClick = (actMenu: MenuState['activeMenu'], routePath: string) => {
    setMenuState(prev => ({ ...prev, activeMenu: actMenu, isOpen: false }));
    navigate(routePath);
  };

  useEffect(() => {
    if (location.pathname.includes('myaccount')) {
      setMenuState(prev => ({ ...prev, activeMenu: 'myaccount' }));
    } else if (window.location.pathname.includes('approvals')) {
      setMenuState(prev => ({ ...prev, activeMenu: 'approvals' }));
    } else {
      setMenuState(prev => ({ ...prev, activeMenu: 'mypasses' }));
    }
    if (passPolicesContext.passPolicies) {
      const policies = passPolicesContext.passPolicies;
      setMenuAccess(prev => ({
        ...prev,
        showMyPasses: policies.some(
          policy =>
            policy.passTypeId === AppConfig.passType.guest ||
            policy.passTypeId === AppConfig.passType.employee,
        ),
        showMyAccount:
          !isETW ||
          (user?.passplayUser?.countryAlpha2?.toUpperCase() === 'KR' &&
            isETW &&
            policies.some(
              policy =>
                policy.passTypeId === AppConfig.passType.employee ||
                policy.passTypeId === AppConfig.passType.swoosh,
            )),
        showMyAccess:
          !isETW ||
          (user?.passplayUser?.countryAlpha2?.toUpperCase() === 'KR' &&
            isETW &&
            policies.some(
              policy =>
                policy.passTypeId === AppConfig.passType.employee ||
                policy.passTypeId === AppConfig.passType.swoosh,
            )),
        showFamily: policies.some(policy => policy.passTypeId === AppConfig.passType.family),
      }));
    }
  }, []);

  return (
    !!passPolicesContext.passPolicies && (
      <div className={`eds-flex eds-flex--justify-content-space-between ${styles.cmdbar_div}`}>
        <div>
          <img alt="Passplay logo" src={logo} title="passplay logo"></img>
        </div>
        <div
          className={`eds-flex eds-flex--flex-direction-row eds-flex--justify-content-flex-end eds-flex--align-items-center ${styles.cmdbar_div}`}
        >
          {menuAccess.showMyPasses && (
            <Button
              data-testid="my-passes-btn"
              className={`menu-txt ${menuState.activeMenu === 'mypasses' ? styles.btn_active : ''}`}
              variant="ghost"
              onClick={() => handleMenuClick('mypasses', '/')}
            >
              {i18nString(ts.HEADER_BAR_MY_PASSES)}
            </Button>
          )}
          {menuAccess.showMyAccount && (
            <Menu
              data-testid="my-account-menu"
              onClickOutside={() => {
                setMenuState(prev => ({ ...prev, isOpen: false }));
              }}
              isOpen={menuState.isOpen}
              bodySlot={
                <>
                  {menuAccess.showMyAccess && (
                    <MenuItem
                      data-testid="my-access"
                      onClick={() => handleMenuClick('myaccount', '/myaccess')}
                      className="menu-txt"
                    >
                      {i18nString(ts.HEADER_BAR_MY_ACCESS)}
                    </MenuItem>
                  )}
                  {menuAccess.showFamily && (
                    <MenuItem
                      data-testid="family"
                      onClick={() => handleMenuClick('myaccount', '/family')}
                      className="menu-txt"
                    >
                      {i18nString(ts.HEADER_BAR_FAMILY)}
                    </MenuItem>
                  )}
                </>
              }
              placement={'bottom-start'}
              onMouseLeave={() => setMenuState(prev => ({ ...prev, isOpen: false }))}
            >
              {menuAccess.showMyAccount && (
                <Button
                  data-testid="my-account-btn"
                  className={`menu-txt ${menuState.activeMenu === 'myaccount' ? styles.btn_active : ''}`}
                  afterSlot={menuState.isOpen ? <CaretUp /> : <CaretDown />}
                  variant="ghost"
                  onClick={() => setMenuState(prev => ({ ...prev, isOpen: true }))}
                  onMouseEnter={() => setMenuState(prev => ({ ...prev, isOpen: true }))}
                >
                  {i18nString(ts.HEADER_BAR_MY_ACCOUNT)}
                </Button>
              )}
            </Menu>
          )}
          {user?.passplayUser?.isApprover &&
            user?.passplayUser?.userCountry.toUpperCase() !== 'NETHERLANDS' && (
              <Button
                data-testid="approvals-btn"
                className={`menu-txt ${menuState.activeMenu === 'approvals' ? styles.btn_active : ''}`}
                variant="ghost"
                onClick={() => handleMenuClick('approvals', '/approvals')}
              >
                {i18nString(ts.HEADER_BAR_APPROVALS)}
              </Button>
            )}
        </div>
        ;
      </div>
    )
  );
};
